<template>
  <div :class="pageClass">
    <StoryblokComponent v-if="story" :blok />
    <NewsletterPopup
      v-if="!isStoryblokEditor && showSubscriptionPopup"
      :blok="subscriptionOverrides"
      :content-type="contentType"
    />
    <NewsletterToggleTrigger
      v-if="!isStoryblokEditor && showSubscriptionPopup"
    />
    <WidgetOgImagePreview v-if="story && isStoryblokEditor" :slug="slug" />
  </div>
</template>

<script setup lang="ts">
import type { StoryType } from '~/types/story'

const route = useRoute()
const slug = useSlug().slugString
const storyProviderStore = useStoryProviderStore()

const pageClass = computed(() => {
  return ['page', ...(story.value?.full_slug?.split('/') ?? [])]
})

const contentType = computed(() => {
  return story?.value?.content?.component
})

// Fetch story from slug
const storyFromSlug = await useStoryblokHelpers().fetchAsyncStory(slug)
// Attempt to create a fallback story if the slug does not exist
const storyFromFallback = ref(null)
if (!storyFromSlug.value) {
  storyFromFallback.value = useStoryblokFallbacks().fallbackStoryForRoute()
}

const storyOrFallback = computed(
  () => storyFromSlug.value || storyFromFallback.value,
)

// TODO - fix this type coercion
storyProviderStore.setPageStory(storyOrFallback as Ref<StoryType>)
const { story } = storeToRefs(storyProviderStore)
const blok = computed(() => story.value?.content)

// Set page meta - note some content types may call this again to set their own meta also
usePageMeta({ blok })

// Check if loaded story exists, or show 404 page
if (!story.value) {
  showError({
    statusCode: 404,
    statusMessage: 'The page you are looking for cannot be found',
    data: {
      slug: route.params.slug,
      full_slug: toValue(slug),
      story: toValue(story),
    },
    fatal: true,
  })
}

// Subscription popup overrides
const { isStoryblokEditor } = useStoryblokHelpers()
const showSubscriptionPopup = computed(
  () => !story.value?.content?.subscription,
)
const subscriptionOverrides = computed(
  () =>
    story.value?.content?.global_overrides?.filter((block) => {
      return block.component === 'BlockSubscriptionSettings'
    })?.[0],
)

// Viewed stories tracking
const { trackView } = useViewTracking()
watch(
  () => route,
  (to, from) => {
    // avoid tracking hash changes
    if (from?.hash !== to?.hash) return
    trackView(story)
  },
)

onMounted(() => {
  trackView(story)
})
</script>
